import React from "react";

import styled from "styled-components";
import { TbCloudUpload, TbInfoCircle } from "react-icons/tb";
import { Flex, Image, Text } from "rebass/styled-components";
import { useState, useRef, useEffect } from "react";
import { useRecoilState } from "recoil";
import { AiFillCloseCircle } from "react-icons/ai";
import axios from "axios";

import theme from "theme/themeThree";
import { BaseSection } from "../styledComponents";
import { Toggle } from "MVVM/Components/Toggle";
import { LoadingSpinner } from "MVVM/Components/Lottie";
import {
  QuickEditPreferredFinalTouches,
  QuickEditPreferredThumbnail,
} from "MVVM/State/quickEdit";
import { usePortal } from "MVVM/Hooks/usePortal";
import { SignatureSelectorModal } from "MVVM/Components/Signatures";
import { analytics } from "MVVM/Analytics";
import { IQuickEditMoment } from "../@types";

export const FinalTouches = ({
  selectedFinalTouches,
  setSelectedFinalTouches,
  setThumbnailKey,
  mediaUploadUrl,
  isOwnMoment,
  moment,
}: {
  selectedFinalTouches: string[];
  setSelectedFinalTouches: (finalTouches: string[]) => void;
  setThumbnailKey: (thumbnailKey: string) => void;
  mediaUploadUrl?: {
    fields: {
      name: string;
      value: string;
    }[];
    key: string;
    url: string;
  };
  isOwnMoment: boolean;
  moment: IQuickEditMoment;
}) => {
  const [selectedFileUrl, setSelectedFileUrl] = useState<string | null>(null);
  const [preferredThumbnail, setPreferredThumbnail] = useRecoilState(
    QuickEditPreferredThumbnail,
  );
  const [preferredFinalTouches, setPreferredFinalTouches] = useRecoilState(
    QuickEditPreferredFinalTouches,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const isSecondary = ["secondary", "secondary_fail"].includes(moment.type);
  const signatureDisabled = !isOwnMoment;
  const rapidFireDisabled = isSecondary;
  const contextCamsDisabled = isSecondary;

  const {
    portalOpen: signatureSelectorOpen,
    setPortalOpen: setSignatureSelectorOpen,
    Portal: SignatureSelectorPortal,
  } = usePortal(
    <SignatureSelectorModal
      onSave={() => {
        setSignatureSelectorOpen(false);
      }}
      onCloseClick={() => {
        setSignatureSelectorOpen(false);
      }}
    />,
  );

  useEffect(() => {
    if (preferredThumbnail) {
      setSelectedFileUrl(preferredThumbnail);
      setThumbnailKey(preferredThumbnail);
    }
  }, [preferredThumbnail, setThumbnailKey]);

  useEffect(() => {
    let preloadedTouches = preferredFinalTouches;
    if (signatureDisabled)
      preloadedTouches = preloadedTouches.filter(
        (t) => t !== "signature" && t !== "music",
      );
    if (rapidFireDisabled)
      preloadedTouches = preloadedTouches.filter((t) => t !== "rapidFire");
    if (contextCamsDisabled)
      preloadedTouches = preloadedTouches.filter((t) => t !== "contextCams");
    setSelectedFinalTouches(preloadedTouches);
  }, [
    preferredFinalTouches,
    signatureDisabled,
    rapidFireDisabled,
    contextCamsDisabled,
    setSelectedFinalTouches,
  ]);

  const { REACT_APP_S3_CLIP_BUCKET } = process.env;

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploading(true);
      const fileUrl = URL.createObjectURL(file);
      const formData = new FormData();

      for (const field of mediaUploadUrl?.fields ?? []) {
        formData.append(field.name, field.value ?? "");
      }
      formData.append("file", file);

      try {
        await axios.post(mediaUploadUrl?.url ?? "", formData);

        setSelectedFileUrl(fileUrl);
        if (mediaUploadUrl?.key) {
          setThumbnailKey(`${mediaUploadUrl.url}/${mediaUploadUrl.key}`);
        }
        setUploading(false);
      } catch (error) {
        setUploading(false);
      }
    }
  };

  const handleThumbnailClick = () => {
    fileInputRef.current?.click();
  };

  const handleToggle = (touch: string) => {
    if (selectedFinalTouches.includes(touch)) {
      setPreferredFinalTouches(
        preferredFinalTouches.filter((t) => t !== touch),
      );
      setSelectedFinalTouches(selectedFinalTouches.filter((t) => t !== touch));
    } else {
      setPreferredFinalTouches([...preferredFinalTouches, touch]);
      setSelectedFinalTouches([...selectedFinalTouches, touch]);
    }
  };

  return (
    <>
      {signatureSelectorOpen && SignatureSelectorPortal}
      <Section>
        <Text
          variant="text.labelSmall"
          style={{
            width: "100%",
            justifyContent: "start",
            color: theme.colors.chalk,
          }}
        >
          Clip Thumbnail:
        </Text>

        <input
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          ref={fileInputRef}
          style={{ display: "none" }}
        />

        <Flex style={{ position: "relative", width: "100%" }}>
          {selectedFileUrl && (
            <AiFillCloseCircle
              style={{
                position: "absolute",
                top: 5,
                right: 5,
                width: 25,
                height: 25,
                cursor: "pointer",
              }}
              color={theme.colors.alert}
              onClick={() => {
                setPreferredThumbnail("");
                setSelectedFileUrl(null);
              }}
            />
          )}
          <ThumbnailArea
            onClick={handleThumbnailClick}
            showBorder={!selectedFileUrl}
          >
            {selectedFileUrl ? (
              <PreviewThumbnail src={selectedFileUrl} />
            ) : (
              <>
                {uploading ? (
                  <LoadingSpinner height={30} width={30} />
                ) : (
                  <Flex
                    flexDirection={"column"}
                    alignItems={"center"}
                    style={{
                      gap: 8,
                    }}
                  >
                    <UploadIcon size={30} />
                    <Text variant="text.labelMedium" color={theme.colors.chalk}>
                      Customize Your Thumbnail
                    </Text>
                    <Text
                      variant="text.labelXXSmall"
                      color={theme.colors.chalk}
                      textAlign={"center"}
                      maxWidth={300}
                    >
                      Upload an optional PNG, JPEG or GIF as a custom thumbnail,
                      or we'll use your Signature or current loadout.
                    </Text>
                  </Flex>
                )}
              </>
            )}
          </ThumbnailArea>
        </Flex>
      </Section>
      <Section pt={0}>
        <Text
          variant="text.labelSmall"
          style={{
            width: "100%",
            justifyContent: "start",
            color: theme.colors.chalk,
          }}
        >
          Finishing Touches:
        </Text>
        <ToggleContainer>
          <ToggleColumn>
            <ToggleRow>
              <Text variant="text.labelSmall">Add My Signature</Text>
              <Toggle
                width={48}
                checked={selectedFinalTouches.includes("signature")}
                disabled={signatureDisabled}
                onChange={() => {
                  handleToggle("signature");
                }}
              />
            </ToggleRow>

            {!signatureDisabled && (
              <ToggleRow
                style={{
                  justifyContent: "start",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <Image
                  src={`${REACT_APP_S3_CLIP_BUCKET}/static/loadouts/sig.svg`}
                  height={10}
                  width={10}
                  mt={1}
                />

                <SignatureText
                  color="chalk"
                  onClick={() => {
                    analytics.track("Signature - Open Modal", {
                      where: "Quick Edit",
                      type: "Manage",
                    });
                    setSignatureSelectorOpen(true);
                  }}
                  variant="text.labelXXSmall"
                >
                  Manage My Signature
                </SignatureText>
              </ToggleRow>
            )}

            {signatureDisabled && (
              <Text
                variant="text.labelXXSmall"
                color={theme.colors.error}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <InfoIcon style={{ marginTop: 2 }} />
                Signatures are not yet supported for Moments that are not your
                own.
              </Text>
            )}
          </ToggleColumn>

          <ToggleColumn>
            <ToggleRow>
              <Text variant="text.labelSmall">Add Signature Music</Text>
              <Toggle
                width={48}
                checked={selectedFinalTouches.includes("music")}
                disabled={!selectedFinalTouches.includes("signature")}
                onChange={() => {
                  handleToggle("music");
                }}
              />
            </ToggleRow>

            {signatureDisabled && (
              <Text
                variant="text.labelXXSmall"
                color={theme.colors.error}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <InfoIcon style={{ marginTop: 2 }} />
                Signatures are not yet supported for Moments that are not your
                own.
              </Text>
            )}
          </ToggleColumn>
          <ToggleColumn>
            <ToggleRow>
              <Text variant="text.labelSmall">Add Context Cams</Text>
              <Toggle
                width={48}
                checked={selectedFinalTouches.includes("contextCams")}
                disabled={contextCamsDisabled}
                onChange={() => {
                  handleToggle("contextCams");
                }}
              />
            </ToggleRow>
            {contextCamsDisabled && (
              <Text
                variant="text.labelXXSmall"
                color={theme.colors.error}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <InfoIcon style={{ marginTop: 2 }} />
                Context Cams are not yet supported for Fail Moments.
              </Text>
            )}
          </ToggleColumn>
          <ToggleColumn>
            <ToggleRow>
              <Text variant="text.labelSmall">Rapid Fire</Text>
              <Toggle
                width={48}
                checked={selectedFinalTouches.includes("rapidFire")}
                disabled={rapidFireDisabled}
                onChange={() => {
                  handleToggle("rapidFire");
                }}
              />
            </ToggleRow>
            {rapidFireDisabled && (
              <Text
                variant="text.labelXXSmall"
                color={theme.colors.error}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <InfoIcon style={{ marginTop: 2 }} />
                Rapid Fire is not yet supported for Fail Moments.
              </Text>
            )}
          </ToggleColumn>
        </ToggleContainer>
      </Section>
    </>
  );
};

const InfoIcon = styled(TbInfoCircle)`
  color: ${theme.colors.error};
`;

const ToggleContainer = styled(Flex)`
  background-color: ${(props) => props.theme.colors.grey825};
  border-radius: 8px;
  flex-direction: column;
  width: 100%;
`;

const ToggleRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;
const ToggleColumn = styled(Flex)`
  align-items: center;
  border-bottom: 1px solid ${(props) => props.theme.colors.ned};
  justify-content: space-between;
  padding: 12px 24px;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  &:last-child {
    border-bottom: none;
  }
`;

const Section = styled(BaseSection)`
  align-items: center;
  border-bottom: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
`;

const PreviewThumbnail = styled(Image)`
  border-radius: 12px;
  height: 100%;
  width: auto;
`;

const UploadIcon = styled(TbCloudUpload)`
  color: ${theme.colors.chalkAlpha80};
`;

const ThumbnailArea = styled(Flex)<{ showBorder: boolean }>`
    ${(props) => (props.showBorder ? theme.colors.chalkAlpha40 : "none")};
  align-items: center;
  aspect-ratio: 16 / 9;
  background-color: ${theme.colors.grey850};
  border-radius: 12px;
  border: 1px dashed
  cursor: pointer;
  height: 100%;
  justify-content: center;
  width: 100%;
  &:hover {
    border: 1px dashed ${theme.colors.envy};
    & > svg {
      color: ${theme.colors.envy};
    }
  }
`;

export const SignatureText = styled(Text)`
  transition: color 100ms ease;
  &:hover {
    cursor: pointer;
    color: ${({ theme }) => theme.colors.envy};
  }
`;
